import { Box, CircularProgress, IconButton, InputAdornment, LinearProgress, TablePagination, TextField, Tooltip, Typography } from "@mui/material"
import { FC, useCallback, useEffect, useState, Fragment } from "react"
import { DeleteOutlineOutlined, EditOutlined, HomeWorkOutlined, LocationOnOutlined, PeopleOutlined, Refresh, SearchOutlined, TrendingUpOutlined, WarningAmberOutlined, CloseOutlined } from "@mui/icons-material"
import { PropertyProps } from "../../types/properties"
import NoData from "../Common/NoData"
import { useNavigate } from "react-router-dom"
import { Apis, getImageUrl, numberWithCommas } from "../../helpers"
import { Connect, mapStateToProps } from "../../Redux"
import DeleteModal from '../Common/deletePrompt'
import _ from 'lodash'

const PRIMARY      = '#8c7c0c'
const PRIMARY_LIGHT= '#f5f2e0'
const WARM_GREEN   = '#6a8c0c'
const WARM_AMBER   = '#d4890a'
const WARM_RED     = '#c05c13'

interface StateProps {
    properties: PropertyProps[]
    showDeleteModal: null | PropertyProps
    loading: boolean
    refreshing: boolean
    searchVal: string
    searching: boolean
    pageSize: number
    page: number
    count: number
}

interface RowProps {
    property: PropertyProps
    onEdit: () => void
    onDelete: () => void
}

const PropertyRow: FC<RowProps> = ({ property, onEdit, onDelete }) => {
    const [imgFailed, setImgFailed] = useState(false)
    const { name, placeName, location, logos, totalUnits, occupiedUnits, expectedRevenue, currentMonthRevenue, arrears } = property
    const occupancyPct = totalUnits > 0 ? Math.round(occupiedUnits * 100 / totalUnits) : 0
    const occupancyColor = occupancyPct >= 80 ? WARM_GREEN : occupancyPct >= 50 ? WARM_AMBER : WARM_RED
    const logoUrl = getImageUrl(logos, 'small')
    const showFallback = !logoUrl || imgFailed
    const initials = name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)

    const stats = [
        { icon: <HomeWorkOutlined sx={{ fontSize: 14 }} />, label: 'Units', value: String(totalUnits), color: PRIMARY },
        { icon: <PeopleOutlined sx={{ fontSize: 14 }} />, label: 'Occupied', value: `${occupancyPct}%`, color: occupancyColor },
        { icon: <TrendingUpOutlined sx={{ fontSize: 14 }} />, label: 'Expected', value: `Ksh ${numberWithCommas(expectedRevenue)}`, color: PRIMARY },
        { icon: <TrendingUpOutlined sx={{ fontSize: 14 }} />, label: 'Collected', value: `Ksh ${numberWithCommas(currentMonthRevenue)}`, color: WARM_GREEN },
        { icon: <WarningAmberOutlined sx={{ fontSize: 14 }} />, label: 'Arrears', value: `Ksh ${numberWithCommas(arrears)}`, color: arrears > 0 ? WARM_RED : WARM_GREEN },
    ]

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, md: 2.5 },
            bgcolor: '#fff',
            borderRadius: 3,
            p: { xs: 2, md: 2 },
            mb: 1.5,
            boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)',
            transition: 'box-shadow .18s, transform .15s',
            '&:hover': { boxShadow: `0 4px 24px rgba(140,124,12,.13)`, transform: 'translateY(-1px)' },
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
        }}>

            {/* Logo */}
            <Box sx={{
                width: { xs: 52, md: 64 },
                height: { xs: 52, md: 64 },
                borderRadius: 2,
                flexShrink: 0,
                overflow: 'hidden',
                border: '2px solid #f0ece0',
                bgcolor: PRIMARY_LIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {!showFallback
                    ? <Box component="img" src={logoUrl} alt={name} onError={() => setImgFailed(true)}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <Typography sx={{ fontWeight: 800, fontSize: 18, color: PRIMARY, lineHeight: 1, userSelect: 'none' }}>
                        {initials}
                      </Typography>
                }
            </Box>

            {/* Name + location + bar */}
            <Box sx={{ flex: { xs: 1, lg: '0 0 220px' }, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }} noWrap>
                    {name}
                </Typography>
                {(placeName || location?.locName) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                        <LocationOnOutlined sx={{ fontSize: 11, color: '#94a3b8' }} />
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>
                            {[placeName, location?.locName].filter(Boolean).join(', ')}
                        </Typography>
                    </Box>
                )}
                <LinearProgress
                    variant="determinate"
                    value={occupancyPct}
                    sx={{
                        mt: 1, height: 3, borderRadius: 2, bgcolor: '#f1f5f9',
                        width: { xs: '100%', lg: 140 },
                        '& .MuiLinearProgress-bar': { bgcolor: occupancyColor, borderRadius: 2 },
                    }}
                />
            </Box>

            {/* Divider */}
            <Box sx={{ display: { xs: 'none', lg: 'block' }, width: '1px', height: 48, bgcolor: '#f1f5f9', flexShrink: 0 }} />

            {/* Stats */}
            <Box sx={{
                display: 'flex',
                gap: { xs: 2, md: 3 },
                flex: 1,
                flexWrap: 'wrap',
                width: { xs: '100%', lg: 'auto' },
            }}>
                {stats.map(({ icon, label, value, color }) => (
                    <Box key={label} sx={{ minWidth: 72 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                            <Box sx={{ color }}>{icon}</Box>
                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                                {label}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color, lineHeight: 1 }}>
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Divider */}
            <Box sx={{ display: { xs: 'none', lg: 'block' }, width: '1px', height: 48, bgcolor: '#f1f5f9', flexShrink: 0 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <Tooltip title="Edit property">
                    <IconButton size="small" onClick={onEdit}
                        sx={{ bgcolor: '#f8fafc', borderRadius: 1.5, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>
                        <EditOutlined sx={{ fontSize: 16, color: '#64748b' }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete property">
                    <IconButton size="small" onClick={onDelete}
                        sx={{ bgcolor: '#fdf2ec', borderRadius: 1.5, '&:hover': { bgcolor: '#fde8dd' } }}>
                        <DeleteOutlineOutlined sx={{ fontSize: 16, color: WARM_RED }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    )
}

const AllProperties: FC = () => {
    const [state, setState] = useState<StateProps>({
        properties: [],
        showDeleteModal: null,
        loading: true,
        refreshing: false,
        searchVal: '',
        searching: false,
        page: 0,
        pageSize: 20,
        count: 0,
    })
    const { properties, loading, showDeleteModal, searchVal, page, pageSize, count, refreshing, searching } = state
    const navigate = useNavigate()

    const searchProperty = (searchVal: string) => getProperties({ searchVal, processVariable: 'searching', newPage: 0 })
    const startSearch = useCallback(_.debounce(searchProperty, 1000), [])

    const getProperties = ({ searchVal, newPage, newPageSize, processVariable }: any) => {
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const NewPage = newPage > -1 ? newPage : state.page
        const NewPageSize = newPageSize || state.pageSize
        Apis({
            module: 'Properties',
            action: 'getPropertiesInfo',
            searchVal,
            page: NewPage,
            pageSize: NewPageSize,
            all: true,
        }).then((resp: any) => {
            const { count, properties } = resp
            setState(s => ({
                ...s, properties,
                count: count || s.count,
                loading: false, searching: false, refreshing: false,
                page: NewPage, pageSize: NewPageSize, searchVal,
            }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false, searching: false, refreshing: false }))
        })
    }

    useEffect(() => {
        getProperties({ processVariable: 'loading' })
    }, [])

    return (
        <Box sx={{ pb: 4, pr: { xs: 2, md: 4 } }}>

            {/* ── Toolbar ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                mb: 3, pb: 3, borderBottom: '1px solid #f0f0f0',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}>
                <Box sx={{ flexShrink: 0, mr: 0.5 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
                        All Properties
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.2, whiteSpace: 'nowrap' }}>
                        {count > 0 ? `${count} ${count === 1 ? 'property' : 'properties'} total` : 'Loading…'}
                    </Typography>
                </Box>

                <Box sx={{ width: '1px', height: 32, bgcolor: '#e5e7eb', flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />

                <TextField
                    size="small"
                    placeholder="Search properties…"
                    value={searchVal}
                    onChange={e => {
                        setState(s => ({ ...s, searchVal: e.target.value }))
                        startSearch(e.target.value)
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                        endAdornment: searchVal ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => { setState(s => ({ ...s, searchVal: '' })); startSearch('') }}>
                                    <CloseOutlined sx={{ fontSize: 16, color: '#9ca3af' }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        flex: 1, minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2.5, bgcolor: '#f9fafb',
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                        },
                    }}
                />

                <Tooltip title="Refresh">
                    <IconButton
                        onClick={() => getProperties({ processVariable: 'refreshing' })}
                        disabled={refreshing}
                        sx={{ border: '1px solid #e5e7eb', borderRadius: 2, bgcolor: '#fff', flexShrink: 0, '&:hover': { bgcolor: '#f9fafb' } }}
                    >
                        {refreshing
                            ? <CircularProgress size={18} sx={{ color: PRIMARY }} />
                            : <Refresh sx={{ fontSize: 20, color: '#6b7280' }} />
                        }
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── List ── */}
            {!properties?.length || loading || searching ? (
                <NoData
                    message="No properties found."
                    searchVal={searchVal}
                    loading={loading || searching}
                />
            ) : (
                <>
                    {properties.map(property => (
                        <PropertyRow
                            key={property._id}
                            property={property}
                            onEdit={() => navigate('/new_property', { state: { item: property } })}
                            onDelete={() => setState(s => ({ ...s, showDeleteModal: property }))}
                        />
                    ))}

                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => getProperties({ newPage, newPageSize: pageSize, processVariable: 'loading', searchVal })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => getProperties({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading', searchVal })}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ mt: 2 }}
                    />
                </>
            )}

            {showDeleteModal && (
                <DeleteModal
                    item={showDeleteModal}
                    close={() => setState(s => ({ ...s, showDeleteModal: null }))}
                    onDelete={() => {
                        const idx = properties.findIndex(q => q._id === showDeleteModal._id)
                        const updated = [...properties]
                        updated.splice(idx, 1)
                        setState(s => ({ ...s, showDeleteModal: null, properties: updated, count: count - 1 }))
                    }}
                    page='Property'
                    module={'Properties'}
                    itemName={showDeleteModal.name}
                    propertyId={showDeleteModal._id}
                />
            )}
        </Box>
    )
}

export default Connect(mapStateToProps)(AllProperties)
